import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Frame, Navigation } from "@shopify/polaris";
import {
  HomeIcon,
  ChatIcon,
  SettingsIcon,
  MobileIcon,
  MegaphoneIcon,
  StarFilledIcon,
  CreditCardIcon,
  EmailIcon,
} from "@shopify/polaris-icons";

interface NavFrameProps {
  readonly children: ReactNode;
}

// Host Frame + side Navigation. Keeping this purely presentational — no data
// fetching, just links. The Shopify Admin shell already gives us a top bar
// with merchant identity and account menu when we're embedded, so we don't
// render our own. The dev sign-in path uses its own pages outside the Frame.
export function NavFrame({ children }: NavFrameProps): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();

  const navigation = (
    <Navigation location={location.pathname}>
      <Navigation.Section
        items={[
          {
            label: "Overview",
            icon: HomeIcon,
            url: "/dashboard",
            selected: location.pathname.startsWith("/dashboard"),
            onClick: () => navigate("/dashboard"),
          },
          {
            label: "Messages",
            icon: ChatIcon,
            url: "/messages",
            selected: location.pathname.startsWith("/messages"),
            onClick: () => navigate("/messages"),
          },
          {
            label: "WhatsApp",
            icon: MobileIcon,
            url: "/whatsapp",
            selected: location.pathname.startsWith("/whatsapp"),
            onClick: () => navigate("/whatsapp"),
          },
          {
            label: "Broadcasts",
            icon: MegaphoneIcon,
            url: "/broadcasts",
            selected: location.pathname.startsWith("/broadcasts"),
            onClick: () => navigate("/broadcasts"),
          },
          {
            label: "Templates",
            icon: EmailIcon,
            url: "/templates",
            selected: location.pathname.startsWith("/templates"),
            onClick: () => navigate("/templates"),
          },
          {
            label: "Feedback",
            icon: StarFilledIcon,
            url: "/feedback",
            selected: location.pathname.startsWith("/feedback"),
            onClick: () => navigate("/feedback"),
          },
          {
            label: "Billing",
            icon: CreditCardIcon,
            url: "/billing",
            selected: location.pathname.startsWith("/billing"),
            onClick: () => navigate("/billing"),
          },
          {
            label: "Settings",
            icon: SettingsIcon,
            url: "/settings",
            selected: location.pathname.startsWith("/settings"),
            onClick: () => navigate("/settings"),
          },
        ]}
      />
    </Navigation>
  );

  return <Frame navigation={navigation}>{children}</Frame>;
}
